import type { ReactNode } from 'react';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { MarkupTool } from './markupShapes';

interface IconProps {
    className?: string;
}

// Drawn on the Heroicons outline grid (24px, 1.5 stroke) so they sit with PencilIcon.
function OutlineIcon({ className, children }: IconProps & { children: ReactNode }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={className}
        >
            {children}
        </svg>
    );
}

function SelectIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <path d="M6 3.75 18.25 12l-5.6 1.35L9.9 18.75 6 3.75Z" />
        </OutlineIcon>
    );
}

function ArrowIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <path d="M5.25 18.75 18.75 5.25M10.5 5.25h8.25v8.25" />
        </OutlineIcon>
    );
}

function LineIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <path d="M5.25 18.75 18.75 5.25" />
        </OutlineIcon>
    );
}

function RectangleIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <rect x="3.75" y="6" width="16.5" height="12" rx="1.5" />
        </OutlineIcon>
    );
}

function EllipseIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <ellipse cx="12" cy="12" rx="8.25" ry="6.25" />
        </OutlineIcon>
    );
}

function TextIcon({ className }: IconProps) {
    return (
        <OutlineIcon className={className}>
            <path d="M5.25 7.5V5.25h13.5V7.5M12 5.25v13.5M9.375 18.75h5.25" />
        </OutlineIcon>
    );
}

function PenIcon({ className }: IconProps) {
    return <PencilIcon aria-hidden="true" className={className} />;
}

const TOOL_ICONS: Record<MarkupTool, (props: IconProps) => JSX.Element> = {
    select: SelectIcon,
    pen: PenIcon,
    arrow: ArrowIcon,
    line: LineIcon,
    rect: RectangleIcon,
    ellipse: EllipseIcon,
    text: TextIcon
};

function ToolIcon({ tool, className }: IconProps & { tool: MarkupTool }) {
    const Icon = TOOL_ICONS[tool];
    return <Icon className={className} />;
}

export { ToolIcon };
